/**
 * "AI Segment Suggestions" accordion — renders ALL pending AI-generated cohorts
 * from localStorage AND drives the live agent run. Sits above the
 * segments-management table.
 *
 * Lifecycle:
 *   - Mounted on the Cohorts page; auto-listens for localStorage updates.
 *   - When the parent calls `triggerRun()` (or via the imperative ref), the
 *     accordion shows a "running" placeholder row + spinner, kicks off
 *     POST /api/run/predictive-segmentation against the agents-console gateway,
 *     and on response appends every cohort to the store. The same accordion
 *     immediately re-renders to show them.
 *
 * Local-only.
 */
import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import {
  Accordion, AccordionItem, AccordionButton, AccordionIcon, AccordionPanel,
  Box, Button, HStack, VStack, Text, Tag, TagLabel, Badge, Code, Divider, Flex,
  Spinner, useToast, Alert, AlertIcon, Tooltip
} from "@chakra-ui/react";
import { FiTrash2 } from "react-icons/fi";
import {
  getSuggestions,
  dismissSuggestion,
  onChange,
  clearSuggestions,
  appendSuggestions
} from "./aiSuggestionsStore";
import { getActiveCustomerId, runAgent, AGENTS_CONSOLE_URL } from "./aiAgentsClient";

// Tenant-agnostic progress labels. Avoid hardcoding any tenant name
// here — the agent is industry-agnostic and the same component runs for
// e-commerce / banking / fintech / logistics / healthcare / retail.
const PHASE_TEXTS = [
  "Discovering your event vocabulary…",
  "Reading distributions + co-occurrence patterns…",
  "Grounding the LLM in your real event data…",
  "Drafting cohort proposals…",
  "Normalizing to canonical RRS conditions…",
  "Self-critique pass…",
  "Estimating audience sizes…"
];

const AISuggestionsAccordion = forwardRef(({ type = "cohort", onUse }, ref) => {
  const customerId = getActiveCustomerId();
  const [list, setList] = useState(() => getSuggestions(type, customerId));
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Subscribe to store changes
  useEffect(() => {
    const off = onChange((d) => {
      if (d.type === type) setList(getSuggestions(type, customerId));
    });
    const onFocus = () => setList(getSuggestions(type, customerId));
    window.addEventListener("focus", onFocus);
    return () => { off(); window.removeEventListener("focus", onFocus); };
  }, [type, customerId]);

  // Cycle phase texts while running
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setPhaseIdx((i) => (i + 1) % PHASE_TEXTS.length), 2500);
    return () => clearInterval(id);
  }, [running]);

  // Track the goal chosen at runtime so the running banner can show it.
  const [activeGoal, setActiveGoal] = useState(null);

  // Imperative trigger — parent calls triggerRun({ business_goal }) to
  // start the agent. business_goal is optional (defaults to "balanced"
  // server-side). The portal's BusinessGoalModal supplies it on click.
  const triggerRun = useCallback(async (opts = {}) => {
    if (running) return;
    const goal = typeof opts === "object" && opts?.business_goal
      ? String(opts.business_goal).toLowerCase()
      : "balanced";
    setActiveGoal(goal);
    setError(null);
    setPhaseIdx(0);
    setRunning(true);
    try {
      const out = await runAgent("predictive-segmentation", {
        // MCP estimator (default in agent config) supports ALL 6
        // condition families. The old local_clickhouse override only
        // sized event/property/behavior cohorts and returned 0 for
        // tag/profile/device/other_contacts/interactions — DA-BUG
        // followup. Letting agent default (mcp_segments_estimate)
        // through gives accurate audience numbers across the board.
        scopes: "mcp:asklytics-agent,mcp:catalog-reader,mcp:analyst-readonly",
        business_goal: goal,
      });
      const result = out?.result;
      if (!result) throw new Error(out?.error || "Agent returned no result");
      const cohorts = (result.cohorts || [])
        // FEAT-0003 — never show critique-rejected or empty-canonical
        // suggestions. The agent now filters these out and surfaces
        // them in result.rejected_candidates[] for audit; this is a
        // defense-in-depth filter on the consumer side.
        .filter((c) => c.kept !== false)
        .filter((c) => Array.isArray(c.canonical) && c.canonical.length > 0)
        .map((c) => ({
          name: c.name,
          description: c.description,
          business_intent: c.business_intent || null,   // DA-BUG-0246 — what business play this cohort enables
          ai_insight: c.ai_insight,
          estimated_use_case: c.estimated_use_case,
          estimated_count: c.estimated_count,
          estimate_pct: c.estimate_pct,
          quality_score: c.quality_score,
          kept: c.kept !== false,
          refresh_frequency: c.refresh_frequency || "daily",
          canonical: c.canonical,
          ui_view: c.ui_view,
          source: "predictive-segmentation",
          generated_at: result.generated_at,
          // v2.2 — explainability fields surfaced as tooltip on each card
          condition_families: c.condition_families,
          confidence_score: c.confidence_score,
          score_breakdown: c.score_breakdown,
          source_signals: c.source_signals,
          goal_alignment: c.goal_alignment,
          alignment_reason: c.alignment_reason,
          effective_score: c.effective_score,
          // DA-FEAT — preserve agent's own cohort id so feedback events
          // (accept/reject) can correlate to the right tbl_agent_run_items row.
          id: c.id || c.cohort_id || null,
        }));
      // DA-FEAT — pass run_id through so each suggestion is correlated
      // with its tbl_agent_runs row for the acceptance/rejection feedback loop.
      const runMeta = { run_id: result?.agent_meta?.run_id || null };
      if (cohorts.length) appendSuggestions(type, customerId, cohorts, runMeta);
      toast({
        title: cohorts.length ? `${cohorts.length} suggestions ready` : "Agent finished with no cohorts",
        status: cohorts.length ? "success" : "warning", duration: 2500
      });
    } catch (e) {
      setError(e.message || "Agent failed");
      toast({ title: "Agent failed", description: e.message, status: "error", duration: 5000 });
    } finally {
      setRunning(false);
    }
  }, [running, type, customerId, toast]);

  useImperativeHandle(ref, () => ({ triggerRun }), [triggerRun]);

  // Don't render the empty box if nothing's happening
  if (!running && !error && (!list || list.length === 0)) return null;

  // FEAT-0003 — counts mirror the rendered list (skip kept=false and
  // empty-canonical so the chip never overstates what the operator sees).
  const shippable = list
    .filter((s) => s.kept !== false)
    .filter((s) => Array.isArray(s.canonical) && s.canonical.length > 0);
  const pending  = shippable.filter((s) => s._status !== "accepted").length;
  const accepted = shippable.filter((s) => s._status === "accepted").length;

  return (
    <Box mb={5} borderRadius="xl" border="1px solid"
         borderColor="purple.100"
         bgGradient="linear(to-r, purple.50, blue.50)"
         overflow="hidden">
      {/* Outer accordion — wraps the whole panel so the operator can */}
      {/* collapse the entire 21-cohort list to just the summary bar.   */}
      <Accordion allowToggle defaultIndex={[0]}>
        <AccordionItem border="none">
          <AccordionButton
            _hover={{ bg: "purple.100" }}
            _expanded={{ borderBottom: "1px solid", borderColor: "purple.100" }}
            px={4} py={3} bg="purple.50">
            <HStack spacing={2} flex={1} textAlign="left">
              <Box w={7} h={7} borderRadius="md" bg="purple.500"
                   display="flex" alignItems="center" justifyContent="center" color="white">
                <span style={{ fontSize: 14 }}>✨</span>
              </Box>
              <Box flex={1}>
                <Text fontWeight="600" fontSize="sm" color="gray.800">AI Segment Suggestions</Text>
                <Text fontSize="xs" color="gray.600">
                  {running
                    ? <span>
                        Running for tenant <strong>customer_id={customerId}</strong>
                        {activeGoal && activeGoal !== "balanced" ? <> · goal: <strong>{activeGoal}</strong></> : null}
                        … <em>{PHASE_TEXTS[phaseIdx]}</em>
                      </span>
                    : `${pending} pending · ${accepted} accepted · grounded in your real event data (cid=${customerId})`}
                </Text>
              </Box>
              {!running && list.length > 0 && (
                <Button size="xs" variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();   // don't toggle accordion
                          clearSuggestions(type, customerId);
                          toast({ title: "All AI suggestions cleared", status: "info", duration: 1500 });
                        }}>
                  Clear all
                </Button>
              )}
              <AccordionIcon />
            </HStack>
          </AccordionButton>

          <AccordionPanel p={0} bg="white">

      {error && (
        <Alert status="error" borderRadius={0} fontSize="xs">
          <AlertIcon />
          <Box flex={1}>
            <Text fontWeight="500">{error}</Text>
            <Text fontSize="11px" color="gray.600">
              Make sure the Agents Console is running at <Code fontSize="11px">{AGENTS_CONSOLE_URL}</Code>
              {" "} (cd xnotify-aiagents/agents-console && npm start)
            </Text>
          </Box>
        </Alert>
      )}

      <Accordion allowMultiple>
        {running && (
          // Pseudo-row that shows while the agent is running
          <AccordionItem border="none" borderBottom="1px solid" borderColor="purple.100" bg="white" isDisabled>
            <Box px={4} py={4}>
              <HStack spacing={3}>
                <Spinner size="sm" color="purple.500" />
                <Box flex={1}>
                  <Text fontWeight="500" fontSize="sm" color="gray.800">Generating AI-grounded segments</Text>
                  <Text fontSize="xs" color="gray.500" mt={0.5}>{PHASE_TEXTS[phaseIdx]}</Text>
                </Box>
                <Tag size="sm" colorScheme="purple"><TagLabel>running</TagLabel></Tag>
              </HStack>
            </Box>
          </AccordionItem>
        )}

        {list
          // FEAT-0003 — never render critique-rejected OR empty-canonical
          // suggestions in the operator-facing list. Pre-existing
          // localStorage entries from older runs are silently filtered
          // here too — the operator never sees an unclickable card.
          .filter((s) => s.kept !== false)
          .filter((s) => Array.isArray(s.canonical) && s.canonical.length > 0)
          .map((s) => (
          <AccordionItem key={s._id} border="none" borderBottom="1px solid" borderColor="purple.100" bg="white">
            <AccordionButton _hover={{ bg: "purple.50" }} px={4} py={3}>
              <HStack flex={1} spacing={3} textAlign="left">
                {s._status === "accepted" ? (
                  <Badge colorScheme="green">accepted</Badge>
                ) : (
                  <Badge colorScheme="purple">new</Badge>
                )}
                <Box flex={1}>
                  <Text fontWeight="600" fontSize="sm">{s.name || "(unnamed)"}</Text>
                  {s.description && <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={1}>{s.description}</Text>}
                </Box>
                {/* DA-BUG-0246 — business_intent tag shows the operator
                    what marketing play this cohort enables. Tag is
                    color-coded so the operator can scan a list of cohorts
                    and immediately see which ones are reactivation
                    vs. conversion vs. retention plays. */}
                {s.business_intent && (
                  <Tag size="sm" colorScheme={BUSINESS_INTENT_COLOR[s.business_intent] || "purple"}>
                    <TagLabel>{BUSINESS_INTENT_LABEL[s.business_intent] || s.business_intent.replace(/_/g, " ")}</TagLabel>
                  </Tag>
                )}
                {s.refresh_frequency && (
                  <Tag size="sm" colorScheme={REFRESH_COLOR[s.refresh_frequency] || "gray"} variant="subtle">
                    <TagLabel>↻ {s.refresh_frequency}</TagLabel>
                  </Tag>
                )}
                {s.quality_score != null && (
                  <Tag size="sm" colorScheme="blue" variant="subtle"><TagLabel>quality {s.quality_score}/10</TagLabel></Tag>
                )}
                {s.estimated_count != null && (
                  <Tag size="sm" variant="subtle"><TagLabel>{fmt(s.estimated_count)} contacts</TagLabel></Tag>
                )}
                {/* Why? — explainability tooltip for FEAT-0002 */}
                {(s.confidence_score != null || s.score_breakdown || s.alignment_reason) && (
                  <Tooltip
                    hasArrow
                    placement="left"
                    bg="gray.800"
                    fontSize="xs"
                    label={
                      <Box maxW="320px">
                        <Text fontWeight="600" mb={1}>Why this segment was suggested</Text>
                        {s.confidence_score != null && (
                          <Text>Confidence: <strong>{s.confidence_score}/100</strong>
                            {s.effective_score && s.effective_score !== s.confidence_score
                              ? <> (effective {s.effective_score}/130 with goal boost)</> : null}
                          </Text>
                        )}
                        {s.score_breakdown && (
                          <Text fontSize="11px" color="gray.300" mt={1}>
                            audience-fit {s.score_breakdown.audience_fit} · variety {s.score_breakdown.dimensional_variety} ·
                            signal {s.score_breakdown.source_signal_strength} · actionability {s.score_breakdown.actionability}
                          </Text>
                        )}
                        {s.alignment_reason && (
                          <Text fontSize="11px" color="purple.200" mt={1}>{s.alignment_reason}</Text>
                        )}
                        {Array.isArray(s.source_signals) && s.source_signals.length > 0 && (
                          <Box mt={2}>
                            <Text fontSize="11px" color="gray.400">Sources:</Text>
                            {s.source_signals.slice(0, 4).map((sig, i) => (
                              <Text key={i} fontSize="11px" color="gray.300">· {sig}</Text>
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                  >
                    <Tag size="sm" variant="outline" colorScheme="purple" cursor="help"
                         onClick={(e) => e.stopPropagation()}>
                      <TagLabel>Why?</TagLabel>
                    </Tag>
                  </Tooltip>
                )}
              </HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4} bg="white">
              {/* Estimated audience — prominent stat card at the top */}
              <HStack mb={3} spacing={2} flexWrap="wrap">
                <Box flex={1} minW="180px" border="1px solid" borderColor="blue.100" bg="blue.50"
                     borderRadius="lg" px={4} py={3}>
                  <Text fontSize="11px" fontWeight="700" color="blue.700" textTransform="uppercase" letterSpacing="wider">
                    Estimated audience
                  </Text>
                  <HStack mt={1} align="baseline" spacing={2}>
                    <Text fontSize="2xl" fontWeight="700" color="blue.700">
                      {s.estimated_count != null ? fmt(s.estimated_count) : "—"}
                    </Text>
                    <Text fontSize="xs" color="blue.600">contacts</Text>
                    {s.estimate_pct != null && (
                      <Text fontSize="xs" color="blue.500">· {s.estimate_pct}% of universe</Text>
                    )}
                  </HStack>
                </Box>
                {s.estimated_use_case && (
                  <Box flex={1} minW="180px" border="1px solid" borderColor="gray.200"
                       borderRadius="lg" px={4} py={3}>
                    <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                      Best used for
                    </Text>
                    <Text fontSize="sm" color="gray.800" mt={1}>{s.estimated_use_case}</Text>
                  </Box>
                )}
                <Box border="1px solid" borderColor="gray.200" borderRadius="lg" px={4} py={3} minW="140px">
                  <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    Refresh
                  </Text>
                  <Text fontSize="sm" color="gray.800" mt={1} fontWeight="500">
                    {s.refresh_frequency ? s.refresh_frequency.charAt(0).toUpperCase() + s.refresh_frequency.slice(1) : "Daily"}
                  </Text>
                </Box>
              </HStack>

              {s.ai_insight && (
                <Box bg="purple.50" borderLeft="3px solid" borderLeftColor="purple.400"
                     borderRadius="md" px={3} py={2} mb={3}>
                  <Text fontSize="xs" color="purple.900" fontStyle="italic">💡 {s.ai_insight}</Text>
                </Box>
              )}
              <FriendlyConditions cohort={s} />
              <Divider my={3} />
              <Flex justify="flex-end" gap={2}>
                <Button size="sm" variant="ghost" colorScheme="red" leftIcon={<FiTrash2 />}
                        onClick={() => { dismissSuggestion(type, customerId, s._id); toast({ title: "Dismissed", status: "info", duration: 1200 }); }}>
                  Dismiss
                </Button>
                {s._status !== "accepted" && (
                  <Button size="sm" colorScheme="blue"
                          onClick={() => {
                            // DO NOT mark accepted here — only when the cohort
                            // is actually saved on /cohorts/create. We just hand
                            // off the suggestion id so the form can call back.
                            onUse && onUse(s);
                          }}>
                    Use this segment
                  </Button>
                )}
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>

          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
});

export default AISuggestionsAccordion;

function fmt(n) { if (n == null) return "—"; return Number(n).toLocaleString(); }

const REFRESH_COLOR = {
  realtime: "red",
  hourly:   "orange",
  daily:    "green",
  weekly:   "blue",
  monthly:  "purple"
};

// DA-BUG-0246 — colour-coded business intent so an operator scanning
// the AI Suggestions list can see at a glance what each cohort enables.
// Same enum as schemas.mjs#BUSINESS_INTENTS.
const BUSINESS_INTENT_COLOR = {
  reactivation:       "orange",
  retention:          "green",
  conversion:         "purple",
  upsell:             "pink",
  cross_sell:         "teal",
  win_back:           "red",
  welcome:            "cyan",
  suppression:        "gray",
  vip_nurturing:      "yellow",
  event_announcement: "blue",
  compliance:         "gray",
};
const BUSINESS_INTENT_LABEL = {
  reactivation:       "Reactivation",
  retention:          "Retention",
  conversion:         "Conversion",
  upsell:             "Upsell",
  cross_sell:         "Cross-sell",
  win_back:           "Win-back",
  welcome:            "Welcome",
  suppression:        "Suppression",
  vip_nurturing:      "VIP nurturing",
  event_announcement: "Event push",
  compliance:         "Compliance",
};

// ─────────────────────────────────────────────────────────────────────────
// Friendly conditions block — turns the agent's canonical / ui_view shape
// into plain-English sentences a non-technical operator can read.
// ─────────────────────────────────────────────────────────────────────────

function FriendlyConditions({ cohort }) {
  // Prefer ui_view from the agent (LLM-friendly form), else derive from canonical.
  const rows = (cohort.ui_view && cohort.ui_view.length)
    ? cohort.ui_view.map(uiViewToSentence)
    : (cohort.canonical || []).map(canonicalToSentence);
  if (!rows.length) {
    return <Text fontSize="xs" color="gray.500">No conditions on this cohort.</Text>;
  }
  return (
    <Box>
      <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
        Who's in this segment
      </Text>
      <VStack align="stretch" spacing={2}>
        {rows.map((r, i) => (
          <HStack key={i} bg="gray.50" borderRadius="md" px={3} py={2.5} spacing={3} align="flex-start">
            <Text fontSize="xs" color="gray.400" fontWeight="700" minW="32px">{i === 0 ? "WHO" : "AND"}</Text>
            <Box flex={1}>
              <Text fontSize="sm" color="gray.800">{r.line}</Text>
              {r.timeframe && (
                <HStack mt={1} spacing={1}>
                  <span style={{ fontSize: 10, color: "#94a3b8" }}>📅</span>
                  <Text fontSize="11px" color="gray.500" fontWeight="500">
                    Window: <strong style={{ color: "#475569" }}>{TIMEFRAME_LABEL[r.timeframe] || r.timeframe}</strong>
                  </Text>
                </HStack>
              )}
              {r.detail && <Text fontSize="11px" color="gray.500" mt={0.5}>{r.detail}</Text>}
            </Box>
            {r.type && <Tag size="sm" variant="subtle" colorScheme="gray"><TagLabel>{r.type}</TagLabel></Tag>}
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}

const OP_TEXT = {
  equals: "is",
  not_equals: "is not",
  greater_than: "is greater than",
  less_than: "is less than",
  greater_than_or_equal: "is at least",
  less_than_or_equal: "is at most",
  contains: "contains",
  starts_with: "starts with",
  ends_with: "ends with"
};

const TIMEFRAME_TEXT = {
  last_7_days:  "in the last 7 days",
  last_14_days: "in the last 14 days",
  last_30_days: "in the last 30 days",
  last_60_days: "in the last 60 days",
  last_90_days: "in the last 90 days",
  all_time:     "ever",
  realtime:     "in real time"
};

// Compact label used in the per-condition "Window" pill
const TIMEFRAME_LABEL = {
  last_7_days:  "Last 7 days",
  last_14_days: "Last 14 days",
  last_30_days: "Last 30 days",
  last_60_days: "Last 60 days",
  last_90_days: "Last 90 days",
  all_time:     "All time",
  realtime:     "Real time"
};

/** Convert one row of agent's `ui_view` into a friendly sentence object.
 *
 * The agent's ui_view rows are self-describing per type:
 *   Event         → { type: "Event", name, property, operator, value, timeframe }
 *   User Property → { type: "User Property", profile_key, operator, value }
 *   Feature       → { type: "Feature", feature_key (or name), operator, value }
 *   Tag           → { type: "Tag", tag, tag_id }
 *   Device        → { type: "Device", device_type }
 *
 * DA-BUG-0242 — earlier versions of this renderer only read `row.name`,
 * which is undefined on User Property / Tag / Device rows, so the UI
 * showed "Their (unknown) is X" or "Has the tag (unknown)". Resolve
 * the human-readable label PER TYPE instead.
 */
function uiViewToSentence(row) {
  const t = (row.type || "").toLowerCase();
  const op = (row.operator || "").toLowerCase();
  const val = row.value;
  const timeframe = row.timeframe;
  // Per-type label resolution — read the field the agent actually emits.
  const label = (() => {
    if (t === "event")          return row.name || row.event;
    if (t === "user property")  return row.profile_key || row.property || row.name;
    if (t === "feature")        return row.feature_key || row.name || row.property;
    if (t === "tag")            return row.tag || row.name || row.tag_id;
    if (t === "device" || t === "device type") return row.device_type || row.channel || row.name;
    return row.name;
  })() || "(unknown)";

  // Special-case the very common "Has done"/"Has not done"/"Has done at least"
  if (t === "event" && (op === "has done" || op === "did")) {
    return { type: "Event", timeframe, line: <>Did <strong>{label}</strong></> };
  }
  if (t === "event" && (op === "has not done" || op === "didn't do" || op === "did not do")) {
    return { type: "Event", timeframe, line: <>Did <strong>NOT</strong> do <strong>{label}</strong></> };
  }
  if (t === "event" && (op === "has done at least" || op === "has done at least n" || op === "count ≥")) {
    return { type: "Event", timeframe, line: <>Did <strong>{label}</strong> at least <strong>{val}</strong> times</> };
  }
  if (t === "event") {
    return { type: "Event", timeframe, line: <>Did <strong>{label}</strong> where <em>{op || "value"}</em> {val}</> };
  }
  if (t === "feature" || t === "user property") {
    return { type: t === "feature" ? "Feature" : "Property",
             line: <>Their <strong>{label}</strong> {OP_TEXT[op] || op || "is"} <strong>{val}</strong></> };
  }
  if (t === "tag") {
    return { type: "Tag", line: <>Has the tag <strong>{label}</strong></> };
  }
  if (t === "device" || t === "device type") {
    return { type: "Device", line: <>On <strong>{label}</strong></> };
  }
  // Fallback
  return { type: row.type, timeframe, line: <>{label} {op} {val}</> };
}

/** Convert one row of the agent's `canonical` shape into a friendly sentence. */
function canonicalToSentence(c) {
  const timeframe = c.timeframe;
  const opPhrase = OP_TEXT[c.operator] || c.operator;
  if (c.type === "event") {
    // count-based
    if (c.property === "count") {
      if (c.operator === "equals" && Number(c.value) === 0) {
        return { type: "Event", timeframe, line: <>Did <strong>NOT</strong> do <strong>{c.event}</strong></> };
      }
      if (c.operator === "greater_than_or_equal" && Number(c.value) === 1) {
        return { type: "Event", timeframe, line: <>Did <strong>{c.event}</strong></> };
      }
      return { type: "Event", timeframe,
               line: <>Did <strong>{c.event}</strong> {opPhrase} <strong>{c.value}</strong> times</> };
    }
    // value on a variable key
    return { type: "Event", timeframe,
             line: <>Did <strong>{c.event}</strong>, where <strong>{c.property}</strong> {opPhrase} <strong>{c.value}</strong></> };
  }
  if (c.type === "customer_profile") {
    return { type: "Property",
             line: <>Their <strong>{c.profile_key || c.property}</strong> {opPhrase} <strong>{c.value}</strong></> };
  }
  if (c.type === "tag") {
    return { type: "Tag", line: <>Has the tag <strong>{c.tag || c.tag_id}</strong></> };
  }
  if (c.type === "device_type") {
    return { type: "Device", line: <>On <strong>{c.device_type || c.channel}</strong></> };
  }
  return { type: c.type, line: JSON.stringify(c) };
}
